import React, { useState, useEffect } from 'react';
import { Loader2, Database, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

/**
 * Admin-only: sync missed Stripe subscription payments into reserve_ledger without curl/JWT copy-paste.
 */
export default function ReserveLedgerRepairModal({ open, onClose, onSuccess, actAsUserId = '' }) {
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [recordLoading, setRecordLoading] = useState(false);
  const [pi, setPi] = useState('');
  const [userId, setUserId] = useState('');
  const [tripId, setTripId] = useState('');
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (open && actAsUserId && String(actAsUserId).trim()) {
      setUserId(String(actAsUserId).trim());
    }
  }, [open, actAsUserId]);

  const handleOpenChange = (next) => {
    if (!next) onClose();
  };

  async function runBackfill() {
    if (
      !window.confirm(
        'Scan every active Reserve trip with a subscription, find paid Stripe invoices missing from the ledger, and insert them? This is safe to run once; existing invoice rows are skipped.'
      )
    ) {
      return;
    }
    setBackfillLoading(true);
    setLastResult(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Not signed in');
        return;
      }
      const res = await fetch('/api/admin/reserve/backfill-payments', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: '{}',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.error || `Request failed (${res.status})`);
        return;
      }
      setLastResult(data);
      toast.success(
        `Backfill finished: ${data.total_credits_inserted ?? 0} ledger credit(s) added across ${data.trips_checked ?? 0} trip(s).`
      );
      onSuccess?.();
    } catch (e) {
      toast.error(e.message || 'Request failed');
    } finally {
      setBackfillLoading(false);
    }
  }

  async function runRecord() {
    const id = pi.trim();
    if (!id.startsWith('pi_')) {
      toast.error('Paste a Stripe Payment Intent ID (starts with pi_)');
      return;
    }
    setRecordLoading(true);
    setLastResult(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Not signed in');
        return;
      }
      const body = { stripe_payment_intent_id: id };
      const uid = userId.trim();
      if (uid) body.user_id = uid;
      const tid = tripId.trim();
      if (tid) body.trip_id = tid;

      const res = await fetch('/api/admin/reserve/record-trip-payment', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.error || `Request failed (${res.status})`);
        return;
      }
      setLastResult(data);
      toast.success('Payment recorded in the ledger.');
      onSuccess?.();
    } catch (e) {
      toast.error(e.message || 'Request failed');
    } finally {
      setRecordLoading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-normal tracking-tight">Ledger sync (admin)</DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            Uses your current login. Run the full scan once, or record a single successful charge from Stripe
            when a payment landed but the ledger did not update.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-white/85">
              <Database className="w-4 h-4 text-emerald-400/90" />
              <span>Full backfill (all active trips)</span>
            </div>
            <p className="text-[13px] text-white/60 leading-relaxed">
              Matches paid subscription invoices in Stripe to your database and adds missing credits labeled
              &quot;Reserve trip payment (backfill)&quot;.
            </p>
            <Button
              type="button"
              variant="outline"
              className="w-full border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              disabled={backfillLoading}
              onClick={runBackfill}
            >
              {backfillLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Running…
                </>
              ) : (
                'Run invoice backfill'
              )}
            </Button>
          </div>

          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-white/85">
              <CreditCard className="w-4 h-4 text-emerald-400/90" />
              <span>One payment (from Payment Intent)</span>
            </div>
            <p className="text-[13px] text-white/60 leading-relaxed">
              Paste the Payment Intent from Stripe (must be succeeded). If auto-match fails, add Trip ID from
              Reserve → All Trips — that always works.
            </p>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Payment Intent ID</Label>
              <Input
                value={pi}
                onChange={(e) => setPi(e.target.value)}
                placeholder="pi_…"
                className="bg-black/40 border-white/10 text-white placeholder:text-white/30 font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Trip ID (use when Stripe does not link the charge)</Label>
              <Input
                value={tripId}
                onChange={(e) => setTripId(e.target.value)}
                placeholder="UUID from All Trips"
                className="bg-black/40 border-white/10 text-white placeholder:text-white/30 font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/60 text-xs">Customer user ID (optional)</Label>
              <Input
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="Prefilled when viewing as customer"
                className="bg-black/40 border-white/10 text-white placeholder:text-white/30 font-mono text-sm"
              />
            </div>
            <Button
              type="button"
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
              disabled={recordLoading}
              onClick={runRecord}
            >
              {recordLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Recording…
                </>
              ) : (
                'Record this payment'
              )}
            </Button>
          </div>

          {lastResult ? (
            <details className="text-xs text-white/40 rounded border border-white/10 p-3 bg-black/20">
              <summary className="cursor-pointer text-white/55 select-none">Last response (JSON)</summary>
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all text-[11px] leading-relaxed">
                {JSON.stringify(lastResult, null, 2)}
              </pre>
            </details>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
